import React, { useState } from 'react';

const daysOfWeek = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const initialSchedules = [
    {
        id: 1,
        name: 'Night Off',
        turnOffTime: '22:00',
        turnOnTime: '06:00',
        duration: null,
        days: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'],
        enabled: true,
        type: 'time', // 'time' or 'duration'
    },
    {
        id: 2,
        name: 'Weekend Shutdown',
        turnOffTime: '23:00',
        turnOnTime: '08:00',
        duration: null,
        days: ['Sat', 'Sun'],
        enabled: false,
        type: 'time',
    },
];

const defaultForm = {
    name: '',
    type: 'time',
    turnOffTime: '22:00',
    turnOnTime: '06:00',
    duration: 2,
    days: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'],
    enabled: true,
};

const AutoOnOffScheduleTab = () => {
    const [schedules, setSchedules] = useState(initialSchedules);
    const [showModal, setShowModal] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(defaultForm);
    const [deleteConfirmId, setDeleteConfirmId] = useState(null);

    const openAdd = () => {
        setForm(defaultForm);
        setEditingId(null);
        setShowModal(true);
    };

    const openEdit = (schedule) => {
        setForm({ ...schedule });
        setEditingId(schedule.id);
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setEditingId(null);
    };

    const toggleDay = (day) => {
        setForm((prev) => ({
            ...prev,
            days: prev.days.includes(day)
                ? prev.days.filter((d) => d !== day)
                : [...prev.days, day],
        }));
    };

    const handleSave = () => {
        if (!form.name.trim()) return;
        if (form.days.length === 0) return;

        if (editingId !== null) {
            setSchedules((prev) =>
                prev.map((s) => (s.id === editingId ? { ...form, id: editingId } : s))
            );
        } else {
            const newId = Date.now();
            setSchedules((prev) => [...prev, { ...form, id: newId }]);
        }
        closeModal();
    };

    const toggleEnabled = (id) => {
        setSchedules((prev) =>
            prev.map((s) => (s.id === id ? { ...s, enabled: !s.enabled } : s))
        );
    };

    const handleDelete = (id) => {
        setSchedules((prev) => prev.filter((s) => s.id !== id));
        setDeleteConfirmId(null);
    };

    const formatScheduleSummary = (s) => {
        if (s.type === 'duration') {
            return `Turns OFF then back ON after ${s.duration}h`;
        }
        return `OFF at ${s.turnOffTime} → ON at ${s.turnOnTime}`;
    };

    return (
        <div className="tab-pane fade show active" id="auto-schedule" role="tabpanel">
            <div className="card">
                <div className="card-header d-flex justify-content-between align-items-center">
                    <button className="btn btn-primary btn-sm" onClick={openAdd}>
                         Add Schedule
                    </button>
                </div>

                <div className="card-body">
                    {schedules.length === 0 ? (
                        <div className="text-center py-5 text-muted">
                            <i className="ti ti-calendar-off" style={{ fontSize: 48 }}></i>
                            <p className="mt-2">No schedules configured. Click <strong>Add Schedule</strong> to get started.</p>
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <table className="table table-hover align-middle">
                                <thead>
                                    <tr>
                                        <th>Schedule Name</th>
                                        <th>Type</th>
                                        <th>Summary</th>
                                        <th>Days</th>
                                        <th>Status</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {schedules.map((s) => (
                                        <tr key={s.id}>
                                            <td>
                                                <strong>{s.name}</strong>
                                            </td>
                                            <td>
                                                <span className={`badge ${s.type === 'duration' ? 'bg-warning text-dark' : 'bg-info text-white'}`}>
                                                    {s.type === 'duration' ? 'Duration' : 'Fixed Time'}
                                                </span>
                                            </td>
                                            <td>
                                                <small className="text-muted">
                                                    <i className="ti ti-clock me-1"></i>
                                                    {formatScheduleSummary(s)}
                                                </small>
                                            </td>
                                            <td>
                                                <div className="d-flex gap-1 flex-wrap">
                                                    {daysOfWeek.map((d) => (
                                                        <span
                                                            key={d}
                                                            className={`badge ${s.days.includes(d) ? 'bg-primary' : 'bg-light text-muted'}`}
                                                            style={{ fontSize: '0.7rem' }}
                                                        >
                                                            {d}
                                                        </span>
                                                    ))}
                                                </div>
                                            </td>
                                            <td>
                                                <div className="form-check form-switch mb-0">
                                                    <input
                                                        className="form-check-input"
                                                        type="checkbox"
                                                        checked={s.enabled}
                                                        onChange={() => toggleEnabled(s.id)}
                                                        id={`toggle-${s.id}`}
                                                    />
                                                    <label className="form-check-label" htmlFor={`toggle-${s.id}`}>
                                                        <span className={`badge ${s.enabled ? 'bg-success' : 'bg-secondary'}`}>
                                                            {s.enabled ? 'Active' : 'Inactive'}
                                                        </span>
                                                    </label>
                                                </div>
                                            </td>
                                            <td>
                                                <button
                                                    className="btn btn-sm btn-outline-primary me-1"
                                                    onClick={() => openEdit(s)}
                                                    title="Edit"
                                                >
                                                    <i className="ti ti-edit"></i>
                                                </button>
                                                <button
                                                    className="btn btn-sm btn-outline-danger"
                                                    onClick={() => setDeleteConfirmId(s.id)}
                                                    title="Delete"
                                                >
                                                    <i className="ti ti-trash"></i>
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {/* Add/Edit Modal */}
            {showModal && (
                <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    <i className="ti ti-calendar-time me-2"></i>
                                    {editingId ? 'Edit Schedule' : 'Add New Schedule'}
                                </h5>
                                <button type="button" className="btn-close" onClick={closeModal}></button>
                            </div>
                            <div className="modal-body">
                                {/* Name */}
                                <div className="mb-3">
                                    <label className="form-label fw-semibold">Schedule Name <span className="text-danger">*</span></label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="e.g. Night Off, Lunch Break"
                                        value={form.name}
                                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                                    />
                                </div>

                                {/* Type */}
                                <div className="mb-3">
                                    <label className="form-label fw-semibold">Schedule Type</label>
                                    <div className="d-flex gap-3">
                                        <div className="form-check">
                                            <input
                                                className="form-check-input"
                                                type="radio"
                                                name="schedType"
                                                id="typeTime"
                                                checked={form.type === 'time'}
                                                onChange={() => setForm({ ...form, type: 'time' })}
                                            />
                                            <label className="form-check-label" htmlFor="typeTime">
                                                Fixed On/Off Time
                                            </label>
                                        </div>
                                        <div className="form-check">
                                            <input
                                                className="form-check-input"
                                                type="radio"
                                                name="schedType"
                                                id="typeDuration"
                                                checked={form.type === 'duration'}
                                                onChange={() => setForm({ ...form, type: 'duration' })}
                                            />
                                            <label className="form-check-label" htmlFor="typeDuration">
                                                Duration (Auto-off then on)
                                            </label>
                                        </div>
                                    </div>
                                </div>

                                {/* Time or Duration fields */}
                                {form.type === 'time' ? (
                                    <div className="row mb-3">
                                        <div className="col-6">
                                            <label className="form-label fw-semibold">Turn OFF At</label>
                                            <input
                                                type="time"
                                                className="form-control"
                                                value={form.turnOffTime}
                                                onChange={(e) => setForm({ ...form, turnOffTime: e.target.value })}
                                            />
                                        </div>
                                        <div className="col-6">
                                            <label className="form-label fw-semibold">Turn ON At</label>
                                            <input
                                                type="time"
                                                className="form-control"
                                                value={form.turnOnTime}
                                                onChange={(e) => setForm({ ...form, turnOnTime: e.target.value })}
                                            />
                                        </div>
                                    </div>
                                ) : (
                                    <div className="mb-3">
                                        <label className="form-label fw-semibold">OFF Duration (hours)</label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            min={0.5}
                                            max={24}
                                            step={0.5}
                                            value={form.duration}
                                            onChange={(e) => setForm({ ...form, duration: parseFloat(e.target.value) })}
                                        />
                                        <div className="form-text">Meter will turn OFF for this many hours, then automatically turn back ON.</div>
                                    </div>
                                )}

                                {/* Days */}
                                <div className="mb-3">
                                    <label className="form-label fw-semibold">Active Days <span className="text-danger">*</span></label>
                                    <div className="d-flex gap-2 flex-wrap">
                                        {daysOfWeek.map((day) => (
                                            <button
                                                key={day}
                                                type="button"
                                                className={`btn btn-sm ${form.days.includes(day) ? 'btn-primary' : 'btn-outline-secondary'}`}
                                                onClick={() => toggleDay(day)}
                                            >
                                                {day}
                                            </button>
                                        ))}
                                    </div>
                                    {form.days.length === 0 && (
                                        <div className="text-danger mt-1" style={{ fontSize: '0.8rem' }}>
                                            Please select at least one day.
                                        </div>
                                    )}
                                </div>

                                {/* Enabled toggle */}
                                <div className="mb-2">
                                    <div className="form-check form-switch">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            id="enabledToggle"
                                            checked={form.enabled}
                                            onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
                                        />
                                        <label className="form-check-label" htmlFor="enabledToggle">
                                            Enable this schedule immediately
                                        </label>
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-secondary" onClick={closeModal}>Cancel</button>
                                <button
                                    className="btn btn-primary"
                                    onClick={handleSave}
                                    disabled={!form.name.trim() || form.days.length === 0}
                                >
                                    <i className="ti ti-device-floppy me-1"></i>
                                    {editingId ? 'Save Changes' : 'Add Schedule'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirm Modal */}
            {deleteConfirmId !== null && (
                <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered modal-sm">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title text-danger">
                                    <i className="ti ti-alert-triangle me-2"></i>Delete Schedule
                                </h5>
                                <button type="button" className="btn-close" onClick={() => setDeleteConfirmId(null)}></button>
                            </div>
                            <div className="modal-body">
                                Are you sure you want to delete this schedule? This action cannot be undone.
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-secondary btn-sm" onClick={() => setDeleteConfirmId(null)}>Cancel</button>
                                <button className="btn btn-danger btn-sm" onClick={() => handleDelete(deleteConfirmId)}>
                                    <i className="ti ti-trash me-1"></i>Delete
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AutoOnOffScheduleTab;